/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 08:54:07
 *
 *******************************************************/
// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_METAFIELDS_REQUEST,
  GET_METAFIELDS_SUCCESS,
  GET_METAFIELDS_FAILURE,
  UPDATE_METAFIELD_REQUEST,
  UPDATE_METAFIELD_SUCCESS,
  UPDATE_METAFIELD_FAILURE,
  DELETE_METAFIELD_REQUEST,
  DELETE_METAFIELD_SUCCESS,
  DELETE_METAFIELD_FAILURE,
  CREATE_METAFIELD_REQUEST,
  CREATE_METAFIELD_SUCCESS,
  CREATE_METAFIELD_FAILURE
} from 'stores/flow-type/metafield.type';
import { trackFSEvent } from 'src/integrations/fullStory';

/**
 * call to get metafields
 * @return {object} The RSAA object for API middleware
 */
export const getMetafields = (i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.metafields.get-metafields',
    { queries: { type: 'metafield_custom_single_metafield' } },
    [
      GET_METAFIELDS_REQUEST,
      {
        type: GET_METAFIELDS_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GET_METAFIELDS_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Metafield.Actions.getMetafields'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to update metafields
 * @return {object} The RSAA object for API middleware
 */
export const updateMetafield = (oldNamespace, oldKey, metafield, i18n: I18n) => {
  // Set params
  const queries = {
    namespace: oldNamespace,
    key: oldKey,
    type: 'metafield_custom_single_metafield'
  };

  return apiService.sendRequest(
    'admin-v3.metafields.update-metafield',
    { queries, body: metafield },
    [
      UPDATE_METAFIELD_REQUEST,
      {
        type: UPDATE_METAFIELD_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom-with-sync',
            i18n.translate('AdminHTML.Metafield.Actions.updateMetafield_Success'),
            i18n
          );
          // Track Full Story event
          trackFSEvent('Edit Metafields', {
            feature: 'Metafields',
            display: metafield?.appliesTo?.display,
            search: metafield?.appliesTo?.search,
            filterOption: metafield?.appliesTo?.filterOption,
            sortByOptions: metafield?.appliesTo?.sortByOption
          });

          return { data, metafield };
        }
      },
      {
        type: UPDATE_METAFIELD_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Metafield.Actions.updateMetafield_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to delete metafield
 * @return {object} The RSAA object for API middleware
 */
export const deleteMetafield = (metafield, i18n: I18n) => {
  const { namespace, key, type = 'metafield_custom_single_metafield' } = metafield;

  const queries = { namespace, key, type };
  return apiService.sendRequest('admin-v3.metafields.delete-metafield', { queries }, [
    DELETE_METAFIELD_REQUEST,
    {
      type: DELETE_METAFIELD_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'custom-with-sync',
          i18n.translate('AdminHTML.Metafield.Actions.deleteMetafield_Success'),
          i18n
        );

        // Track Full Story event
        trackFSEvent('Delete Metafields', {
          feature: 'Metafields',
          display: metafield?.appliesTo?.display,
          search: metafield?.appliesTo?.search,
          filterOption: metafield?.appliesTo?.filterOption,
          sortByOptions: metafield?.appliesTo?.sortByOption
        });

        return { data };
      }
    },
    {
      type: DELETE_METAFIELD_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Metafield.Actions.deleteMetafield_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to create metafield
 * @return {object} The RSAA object for API middleware
 */
export const createMetafield = (metafield, i18n: I18n) => {
  // Set params
  const queries = { namespace: '', key: '' };

  return apiService.sendRequest(
    'admin-v3.metafields.update-metafield',
    { queries, body: metafield },
    [
      CREATE_METAFIELD_REQUEST,
      {
        type: CREATE_METAFIELD_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'custom-with-sync',
            i18n.translate('AdminHTML.Metafield.Actions.createMetafield_Success'),
            i18n
          );
          // Track Full Story event
          trackFSEvent('Add Metafields', {
            feature: 'Metafields',
            display: metafield?.appliesTo?.display,
            search: metafield?.appliesTo?.search,
            filterOption: metafield?.appliesTo?.filterOption,
            sortByOptions: metafield?.appliesTo?.sortByOption
          });

          return { data, metafield };
        }
      },
      {
        type: CREATE_METAFIELD_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.Metafield.Actions.createMetafield_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};
